/**
 * inboundTaxDecision — PURE Effektiv-Scheme-Resolver für die manuelle
 * Steuerkontierung (architecture-eingang-steuerkontierung.md §5.2).
 *
 * `resolveEffectiveTaxScheme(doc)` verdichtet den automatisch abgeleiteten
 * `doc.taxScheme` und eine ggf. aktive manuelle Entscheidung (`doc.taxDecision`)
 * auf DAS Scheme, das Badges/Hints verwenden sollen — plus die Herkunft
 * (`auto`/`manual`) und einen `stale`-Marker.
 *
 * Fail-closed (Muster Fingerprint-Reaktivität): eine Entscheidung, die sich auf
 * einen anderen Auswertungsstand des Belegs bezog (anderer `sha256` oder anderer
 * Auto-Wert), oder ein unbekanntes gespeichertes Scheme, zählt als STALE →
 * Anzeige fällt auf den Auto-Pfad (`'unknown'` ⇒ „Bitte prüfen") zurück, NIE auf
 * einen stillen Manuell-Wert.
 *
 * Bestand bleibt byte-identisch: ohne `taxDecision` liefert der Resolver exakt
 * `{ scheme: doc.taxScheme ?? 'unknown', source: 'auto' }` — alle bestehenden
 * Badge-/Hint-Tests bleiben unverändert grün.
 *
 * @module inboundTaxDecision
 */

import type {
  InboundDocument,
  InboundTaxDecision,
  InboundTaxScheme,
} from '../types/inboundTypes';

/** Herkunft des effektiven Schemes. */
export type EffectiveTaxSchemeSource = 'auto' | 'manual';

/** Ergebnis von {@link resolveEffectiveTaxScheme}. */
export interface EffectiveTaxScheme {
  /** Das Scheme, das Badges/Hints verwenden sollen. */
  scheme: InboundTaxScheme;
  source: EffectiveTaxSchemeSource;
  /** Die aktive Entscheidung (nur bei `source==='manual'`), sonst `null`. */
  decision: InboundTaxDecision | null;
  /** True: Entscheidung existiert, matcht aber den Beleg-Zustand nicht mehr. */
  stale: boolean;
}

/**
 * Wählbare Override-Ziele (Allowlist §4.1) — Spiegel der Functions-SoT
 * (`INBOUND_TAX_DECISION_SELECTABLE`). Reihenfolge = Panel-Select-Reihenfolge.
 */
export const INBOUND_TAX_DECISION_SELECTABLE: readonly InboundTaxScheme[] = [
  'standard',
  'reverse_charge',
  'intra_community',
  'tax_exempt',
  'zero_rated',
  'export',
  'out_of_scope',
];

/** §13b/IG-Pflicht-Kategorien mit möglicher Steuerschuldnerschaft (Jurist J-2). */
const REVERSE_LIABILITY_SCHEMES: ReadonlySet<InboundTaxScheme> = new Set<InboundTaxScheme>([
  'reverse_charge',
  'reverse_charge_mixed',
  'intra_community',
]);

/**
 * J-2-Regel (gespiegelt vom Server für die UX-Pflicht-Markierung): verlangt der
 * Weg-Override von einer §13b/IG-Pflicht-Kategorie eine Begründung? PURE.
 *
 * @param autoScheme das automatisch abgeleitete Scheme.
 * @param targetScheme das gewählte Ziel-Scheme.
 * @returns `true`, wenn eine Begründung Pflicht ist.
 */
export function noteRequiredFor(
  autoScheme: InboundTaxScheme,
  targetScheme: InboundTaxScheme,
): boolean {
  return (
    REVERSE_LIABILITY_SCHEMES.has(autoScheme) &&
    !REVERSE_LIABILITY_SCHEMES.has(targetScheme)
  );
}

/** Alle bekannten Schemes (fail-closed-Erkennung eines unbekannten DB-Strings). */
const KNOWN_SCHEMES: ReadonlySet<string> = new Set<InboundTaxScheme>([
  'standard',
  'reverse_charge',
  'reverse_charge_mixed',
  'intra_community',
  'tax_exempt',
  'zero_rated',
  'export',
  'out_of_scope',
  'mixed',
  'unknown',
]);

/** Auto-Wert des Belegs, fail-closed auf `'unknown'`. */
function autoSchemeOf(doc: InboundDocument): InboundTaxScheme {
  return doc.taxScheme ?? 'unknown';
}

/**
 * True, wenn die Entscheidung nicht mehr zum aktuellen Beleg-Zustand passt
 * (§5.2 Staleness-Regel). Bewusst tolerant: fehlende Anker (`basedOnSha256` oder
 * `doc.sha256` `null`) lösen KEINE Staleness aus (der Sha-Vergleich greift nur,
 * wenn beide Seiten einen Wert tragen).
 */
function isStale(
  decision: InboundTaxDecision,
  doc: InboundDocument,
): boolean {
  // Unbekanntes gespeichertes Scheme ⇒ fail-closed stale.
  if (!KNOWN_SCHEMES.has(decision.scheme)) return true;
  // sha256-Drift (nur wenn beide Seiten einen Anker haben).
  if (
    decision.basedOnSha256 &&
    doc.sha256 &&
    decision.basedOnSha256 !== doc.sha256
  ) {
    return true;
  }
  // Auto-Wert-Drift: der Beleg wurde neu ausgewertet und trägt jetzt einen
  // anderen Auto-Wert als zum Entscheidungszeitpunkt.
  if (decision.previousAutoScheme !== autoSchemeOf(doc)) return true;
  return false;
}

/**
 * Löst das effektive Steuer-Scheme eines Eingangsbelegs auf. PURE.
 *
 * @param doc der Eingangsbeleg.
 * @returns {@link EffectiveTaxScheme} — bei aktiver, nicht-staler Entscheidung
 *   `source:'manual'` + deren Scheme; sonst der Auto-Pfad (`source:'auto'`).
 */
export function resolveEffectiveTaxScheme(
  doc: InboundDocument,
): EffectiveTaxScheme {
  const auto = autoSchemeOf(doc);
  const decision =
    doc.taxDecision && typeof doc.taxDecision === 'object'
      ? doc.taxDecision
      : null;

  if (decision === null) {
    return { scheme: auto, source: 'auto', decision: null, stale: false };
  }

  if (isStale(decision, doc)) {
    // Entscheidung existiert, gilt aber nicht mehr → Auto-Pfad, stale-Marker.
    return { scheme: auto, source: 'auto', decision, stale: true };
  }

  return {
    scheme: decision.scheme,
    source: 'manual',
    decision,
    stale: false,
  };
}
